import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  companyMachineUseCase,
  companyStatisticsUseCase,
  companyUseCase,
} from "../../../di/container";
import { useParams, useNavigate } from "react-router-dom";
import type { MachineDetail } from "../../../domain/types/machine-detail";
import { CompanyOrderRepositoryImpl } from "../../../infrastructure/repositories/company/company-order-repository-impl";
import { CompanyOrderUsecase } from "../../../domain/usecases/company/company-order-usecase";
import type { Order } from "../../pages/CompanyAdmin/OrderManagement/types";
import type {
  Courier,
  OrderOptionResponse,
} from "../../../domain/types/orders";
import {
  mapApiOrderToView,
  uiStatusToApiStatus,
} from "../../pages/CompanyAdmin/OrderManagement/utils";
import { ResubmissionDialog } from "../../pages/CompanyAdmin/OrderManagement/components/ResubmissionDialog";
import { ShippingDialog } from "../../pages/CompanyAdmin/OrderManagement/components/ShippingDialog";
import { ErrorMessageModal } from "../UI/ErrorMessageModal";
import { CompanyOnboardingPanel } from "./Onboarding/CompanyOnboardingPanel";
import { fallbackKpiData, kpiData } from "../../mock-data/kpiData";
import { useAuthStore } from "../../stores/auth-store";
import z from "zod";
import type { VisitorStatsResponse } from "../../../domain/types/company/company-statistics";
import type {
  OrderStatsResponse,
  PaymentStatsResponse,
  RegistrationStatsResponse,
} from "../../../shared/types/api";

// 칸반 상태 타입 (6개 컬럼)
type KanbanStatus =
  | "accepted"
  | "resubmission"
  | "production"
  | "production_complete"
  | "shipping"
  | "delivered";

const KANBAN_STATUS_LABELS: Record<KanbanStatus, string> = {
  accepted: "접수",
  resubmission: "재접수",
  production: "제작",
  production_complete: "제작 완료",
  shipping: "출고/배송",
  delivered: "배송 완료",
};

// 컬럼별 배경색
const getColumnBgColor = (columnId: KanbanStatus): string => {
  switch (columnId) {
    case "accepted":
    case "resubmission":
      return "#F2F2F2";
    case "production":
    case "production_complete":
      return "#F5F8FF";
    case "shipping":
    case "delivered":
      return "#E8EFFF";
    default:
      return "#F2F2F2";
  }
};

// 카드 타이틀 색상
const getTitleColor = (status: KanbanStatus): string => {
  switch (status) {
    case "production":
    case "production_complete":
    case "shipping":
    case "delivered":
      return "#4285F4";
    default:
      return "#111827";
  }
};

interface KanbanCard {
  id: string;
  title: string;
  status: KanbanStatus;
  orderNumber: string;
  customerName: string;
  orderDate: string;
  quantity: number;
  options?: OrderOptionResponse[];
}

interface KanbanColumn {
  id: KanbanStatus;
  title: string;
  count: number;
  cards: KanbanCard[];
}

// Order를 KanbanCard로 변환
const orderToKanbanCard = (order: Order): KanbanCard | null => {
  const validStatuses: KanbanStatus[] = [
    "accepted",
    "resubmission",
    "production",
    "production_complete",
    "shipping",
    "delivered",
  ];

  if (!validStatuses.includes(order.status as KanbanStatus)) {
    return null;
  }

  return {
    id: order.id,
    title: order.productName,
    status: order.status as KanbanStatus,
    orderNumber: order.orderNumber,
    customerName: order.customerName,
    orderDate: order.orderDate,
    quantity: order.quantity,
    options: order.options,
  };
};

// 드롭 가능한 컬럼 컴포넌트
const DroppableColumn: React.FC<{
  column: KanbanColumn;
  onCardClick: (cardId: string) => void;
  collapsedCards: Set<string>;
  onToggleCard: (cardId: string) => void;
}> = ({ column, onCardClick, collapsedCards, onToggleCard }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const bgColor = getColumnBgColor(column.id);

  return (
    <div className="flex flex-col p-[1.5px] min-w-[240px] w-[240px] flex-shrink-0">
      <div className="flex items-center gap-1.5 px-1.5 mb-1.5">
        <h3 className="text-[13px] font-semibold text-[#333]">{column.title}</h3>
        <span className="min-w-[20px] px-1.5 rounded-full bg-[#F2F2F2] text-[11px] leading-[18px] text-center text-[#777] tabular-nums">
          {column.count}
        </span>
      </div>

      <div
        ref={setNodeRef}
        className={`overflow-hidden min-h-[30rem] h-full rounded-[10px] p-2 transition-colors ${
          isOver ? "bg-blue-50 ring-2 ring-blue-300" : ""
        }`}
        style={{ backgroundColor: isOver ? undefined : bgColor }}
      >
        <div className="relative">
          <SortableContext
            items={column.cards.map((card) => card.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 min-h-[100px]">
              {column.cards.length === 0 && (
                <div className="pt-8 text-center text-[12px] text-[#BBBBBB]">작업 없음</div>
              )}
              {column.cards.map((card) => (
                <DraggableCard
                  key={card.id}
                  card={card}
                  onClick={() => onCardClick(card.id)}
                  isExpanded={!collapsedCards.has(card.id)}
                  onToggle={() => onToggleCard(card.id)}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

// 드래그 가능한 카드 컴포넌트
const DraggableCard: React.FC<{
  card: KanbanCard;
  onClick: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ card, onClick, isExpanded, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // 드래그 중이 아닐 때만 클릭 이벤트 발생
    if (!isDragging) {
      onClick();
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className="bg-white rounded-[8px] px-3 py-2.5 border border-black/[0.04] shadow-[0_1px_2px_rgba(0,0,0,0.05)] cursor-grab active:cursor-grabbing hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow w-full"
    >
      <div className="flex items-center justify-between">
        <h4
          className="text-[13px] font-semibold truncate text-left flex-1"
          style={{ color: getTitleColor(card.status) }}
        >
          {card.title}
        </h4>
        <button
          onClick={handleToggle}
          className="ml-2 -mr-1 p-1 rounded-[6px] text-gray-400 hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label={isExpanded ? "접기" : "펼치기"}
        >
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
      <div
        className={`text-[12px] leading-[18px] text-[#444] space-y-0.5 overflow-hidden transition-all duration-200 ${
          isExpanded ? "max-h-[500px] opacity-100 mt-1.5" : "max-h-0 opacity-0"
        }`}
      >
        <div className="flex justify-between">
          <span className="text-[#999]">주문번호</span>
          <span className="font-medium">{card.orderNumber}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#999]">주문자</span>
          <span>{card.customerName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#999]">주문일</span>
          <span>{card.orderDate}</span>
        </div>
        {card.options && card.options.length > 0 && (
          <div className="pt-1 mt-1 border-t border-[#F2F2F2] space-y-1">
            {card.options.map((opt, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-[#999]">{opt.optionName}</span>
                <span className="font-medium">{opt.optionValue}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between pt-1 mt-1 border-t border-gray-100">
          <span className="text-[#999]">주문수량</span>
          <span className="font-medium">{card.quantity}개</span>
        </div>
      </div>
    </div>
  );
};

// 카드 오버레이 (드래그 중 표시)
const CardOverlay: React.FC<{ card: KanbanCard }> = ({ card }) => (
  <div
    className="bg-white rounded-[8px] px-3 py-2.5 shadow-[0_8px_24px_rgba(66,133,244,0.18)] border border-[#4285F4]/40 rotate-[1.5deg]"
    style={{ minWidth: "220px", minHeight: "100px" }}
  >
    <h4
      className="text-[13px] font-semibold mb-1.5 truncate text-left"
      style={{ color: getTitleColor(card.status) }}
    >
      {card.title}
    </h4>
    <div className="text-[12px] leading-[18px] text-[#444] space-y-0.5">
      <div className="flex justify-between">
        <span className="text-[#999]">주문번호</span>
        <span className="font-medium">{card.orderNumber}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-[#999]">주문자</span>
        <span>{card.customerName}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-[#999]">주문일</span>
        <span>{card.orderDate}</span>
      </div>
      {card.options && card.options.length > 0 && (
        <div className="pt-1 mt-1 border-t border-[#F2F2F2] space-y-1">
          {card.options.map((opt, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="text-[#999]">{opt.optionName}</span>
              <span className="font-medium">{opt.optionValue}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between pt-1 mt-1 border-t border-gray-100">
        <span className="text-[#999]">주문수량</span>
        <span className="font-medium">{card.quantity}개</span>
      </div>
    </div>
  </div>
);

type ActiveTabKorean =
  | "장비가동량"
  | "결제건수"
  | "결제금액"
  | "접속자수"
  | "가입자수"
  | "주문량";
type ActiveTabEnglish =
  | "equipmentOperationCount"
  | "paymentCount"
  | "paymentAmount"
  | "visitorCount"
  | "customerCount"
  | "orderCount";

export const CompanyAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTabKorean>("접속자수");
  const [activeId, setActiveId] = useState<string | null>(null);

  const { companyId } = useParams();

  const [visitorStats, setVisitorStats] = useState<VisitorStatsResponse | null>(
    null,
  );
  const [registrationStats, setRegistrationStats] =
    useState<RegistrationStatsResponse | null>(null);
  const [orderStats, setOrderStats] = useState<OrderStatsResponse | null>(null);
  const [paymentStats, setPaymentStats] = useState<PaymentStatsResponse | null>()
  useEffect(() => {
    (async () => {
      if (!companyId) return;

      const today = new Date();
      const endDate = today.toISOString().split("T")[0];
      const startDate = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];

      const visitorStatsResponse =
        await companyStatisticsUseCase.getVisitorStats(
          Number(companyId),
          "DAILY",
          startDate,
          endDate,
        );
      setVisitorStats(visitorStatsResponse);

      const registrationStatsResponse =
        await companyStatisticsUseCase.getRegistrationStats(
          Number(companyId),
          "DAILY",
          startDate,
          endDate,
        );
      setRegistrationStats(registrationStatsResponse);

      const orderStatsResponse = await companyStatisticsUseCase.getOrderStats(
        Number(companyId),
        "DAILY",
        startDate,
        endDate,
      );
      setOrderStats(orderStatsResponse);

      const paymentStatsResponse = await companyStatisticsUseCase.getPaymentStats(
        Number(companyId),
        "DAILY",
        startDate,
        endDate,
      );
      setPaymentStats(paymentStatsResponse);
    })();
  }, [companyId]);
  const navigate = useNavigate();

  // API 연동
  const repo = useMemo(() => new CompanyOrderRepositoryImpl(), []);
  const usecase = useMemo(() => new CompanyOrderUsecase(repo), [repo]);

  // 주문 데이터
  const [orders, setOrders] = useState<Order[]>([]);
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[]>([
    { id: "accepted", title: "접수", count: 0, cards: [] },
    { id: "resubmission", title: "재접수", count: 0, cards: [] },
    { id: "production", title: "제작", count: 0, cards: [] },
    { id: "production_complete", title: "제작 완료", count: 0, cards: [] },
    { id: "shipping", title: "출고/배송", count: 0, cards: [] },
    { id: "delivered", title: "배송 완료", count: 0, cards: [] },
  ]);

  // 다이얼로그 상태
  const [isResubmissionOpen, setIsResubmissionOpen] = useState(false);
  const [resubmissionMessage, setResubmissionMessage] = useState("");
  const [isShippingDialogOpen, setIsShippingDialogOpen] = useState(false);
  const [shippingCourier, setShippingCourier] = useState("");
  const [shippingTrackingNumber, setShippingTrackingNumber] = useState("");
  const [courierList, setCourierList] = useState<Courier[]>([]);
  const [isCourierLoading, setIsCourierLoading] = useState(false);

  // 드래그 중인 카드 및 대상 정보
  const [pendingMove, setPendingMove] = useState<{
    cardId: string;
    targetColumn: KanbanStatus;
  } | null>(null);

  // 에러 모달
  const [errorMessageTitle, setErrorMessageTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 칸반 스크롤 관련
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 카드 접힘 상태 관리 (기본은 열림)
  const COLLAPSED_CARDS_KEY = `kanban_collapsed_cards_${companyId}`;

  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem(COLLAPSED_CARDS_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  const handleToggleCard = useCallback(
    (cardId: string) => {
      setCollapsedCards((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(cardId)) {
          newSet.delete(cardId);
        } else {
          newSet.add(cardId);
        }
        sessionStorage.setItem(
          COLLAPSED_CARDS_KEY,
          JSON.stringify(Array.from(newSet)),
        );
        return newSet;
      });
    },
    [COLLAPSED_CARDS_KEY],
  );

  const COLUMN_WIDTH = 250; // 240px + 10px gap

  // 스크롤 가능 여부 체크
  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // 스크롤 이동 핸들러
  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  // 스크롤 이벤트 리스너 및 초기 체크
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollability();
    container.addEventListener("scroll", checkScrollability);
    window.addEventListener("resize", checkScrollability);

    return () => {
      container.removeEventListener("scroll", checkScrollability);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability, kanbanColumns]);

  const activeTabNames: Record<ActiveTabKorean, ActiveTabEnglish> = {
    장비가동량: "equipmentOperationCount",
    결제건수: "paymentCount",
    결제금액: "paymentAmount",
    접속자수: "visitorCount",
    가입자수: "customerCount",
    주문량: "orderCount",
  };

  interface ChartData {
    date: string;
    equipmentOperationCount: number;
    paymentAmount: number;
    paymentCount: number;
    visitorCount: number;
    customerCount: number;
    orderCount: number;
  }
  const [chartDataArray, setChartDataArray] = useState<ChartData[]>([]);
  const [companyMachines, setCompanyMachines] = useState<MachineDetail[]>([]);

  const { user } = useAuthStore();

  const selectedKpiData =
    kpiData.filter((data) => data.companyId === Number(user?.companyId))[0] ??
    fallbackKpiData;
  const metrics = selectedKpiData.metrics;

  const [usingName, setUsingName] = useState("");

  useEffect(() => {
    if (selectedKpiData.dashBoardGraph === "PRODUCTION") {
      setUsingName("생산량");
    } else if (selectedKpiData.dashBoardGraph === "LEAD_TIME") {
      setUsingName("수주출하 리드타임");
    } else if (selectedKpiData.dashBoardGraph === "OPERATION_RATE") {
      setUsingName("가동량");
    } else if (selectedKpiData.dashBoardGraph === "DELIVERY_RATE") {
      setUsingName("납기 준수율");
    } else if (selectedKpiData.dashBoardGraph === "VISITOR_COUNT") {
      setUsingName("접속자수");
    } else if (selectedKpiData.dashBoardGraph === "CUSTOMER_COUNT") {
      setUsingName("가입자수");
    } else if (selectedKpiData.dashBoardGraph === "ORDER_COUNT") {
      setUsingName("주문량");
    } else if (selectedKpiData.dashBoardGraph === "PAYMENT_AMOUNT") {
      setUsingName("결제금액");
    }
  }, []);

  // 차트 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        // const a = await companyUseCase.getCompanyRealtimeGraph(
        //   Number(companyId),
        // );

        interface OnePieceOfData {
          date: string;
          paymentAmount: number;
          visitorCount: number;
          customerCount: number;
          orderCount: number;
          paymentCount: number; // put 0
          equipmentOperationCount: number; // put 0
          dailyProduction: number; // put 0
          dailyLeadTime: number; // put 0
          operationRate: number; // put 0
          deliveryComplianceRate: number; // put 0
        }

        const mockData = [
          {
            paymentAmount: 120000,
            visitorCount: 200,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 85000,
            visitorCount: 220,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 94000,
            visitorCount: 180,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 150000,
            visitorCount: 220,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 73000,
            visitorCount: 214,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 200000,
            visitorCount: 183,
            customerCount: 2,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 50000,
            visitorCount: 192,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 110000,
            visitorCount: 203,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 68000,
            visitorCount: 206,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 175000,
            visitorCount: 209,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 92000,
            visitorCount: 210,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 45000,
            visitorCount: 215,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 130000,
            visitorCount: 220,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 39000,
            visitorCount: 201,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 210000,
            visitorCount: 197,
            customerCount: 2,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 60000,
            visitorCount: 177,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 95000,
            visitorCount: 189,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 180000,
            visitorCount: 184,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 72000,
            visitorCount: 191,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 160000,
            visitorCount: 182,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 54000,
            visitorCount: 197,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 140000,
            visitorCount: 203,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 48000,
            visitorCount: 204,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 175000,
            visitorCount: 208,
            customerCount: 1,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 35000,
            visitorCount: 209,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 115000,
            visitorCount: 203,
            customerCount: 0,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 88000,
            visitorCount: 222,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 220000,
            visitorCount: 215,
            customerCount: 1,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 90000,
            visitorCount: 205,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            paymentAmount: 130000,
            visitorCount: 189,
            customerCount: 0,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
        ];

        const today = new Date();
        let count = 0;

        let data: OnePieceOfData[] = [];

        for (let i = 0; i < 15; i++) {
          const currentDate = new Date(
            today.getTime() - count * 24 * 60 * 60 * 1000,
          );
          const oneVisitorStat = visitorStats?.stats.filter(
            (item) => item.date === currentDate.toISOString().slice(0, 10),
          )[0];
          const oneRegistrationStat = registrationStats?.stats.filter(
            (item) => item.date === currentDate.toISOString().slice(0, 10),
          )[0];
          const oneOrderStat = orderStats?.stats.filter(
            (item) => item.date === currentDate.toISOString().slice(0, 10),
          )[0];
          const onePaymentStat = paymentStats?.stats.filter(
            (item) => item.date === currentDate.toISOString().slice(0, 10),
          )[0];
          data.push({
            date: currentDate.toISOString().split("T")[0],
            ...mockData[i],
            visitorCount:
              oneVisitorStat?.count && oneVisitorStat?.count !== 0
                ? oneVisitorStat?.count
                : 0,
            customerCount:
              oneRegistrationStat?.count && oneRegistrationStat?.count !== 0
                ? oneRegistrationStat?.count
                : 0,
            orderCount:
              oneOrderStat?.count && oneOrderStat?.count !== 0
                  ? oneOrderStat?.count
                  : 0,
            paymentAmount:
              onePaymentStat?.amount && onePaymentStat?.amount !== 0
                  ? onePaymentStat?.amount
                  : 0,
          });
          count++;
        }

        const formatDateToDH = (dateString: string): string => {
          const date = new Date(dateString);
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${month}.${day}`;
        };

        let sortedData = [...data].sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });

        sortedData = sortedData.reverse();
        sortedData = sortedData.slice(1, 16);
        sortedData = sortedData.reverse();

        sortedData = sortedData.map((item) => ({
          ...item,
          date: formatDateToDH(item.date),
        }));

        sortedData = sortedData.map((item) => {
          return { ...item, equipmentOperationCount: 0 };
        });

        sortedData = sortedData.map((item) => {
          for (let i = 0; i < selectedKpiData.graphData.length; i++) {
            const replacedDate = selectedKpiData.graphData[i].date.replace(
              "/",
              ".",
            );
            if (item.date === replacedDate) {
              if (selectedKpiData.dashBoardGraph === "PRODUCTION") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].dailyProduction,
                };
              } else if (selectedKpiData.dashBoardGraph === "LEAD_TIME") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].dailyLeadTime,
                };
              } else if (selectedKpiData.dashBoardGraph === "OPERATION_RATE") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].operationRate,
                };
              }
              if (selectedKpiData.dashBoardGraph === "DELIVERY_RATE") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].deliveryComplianceRate,
                };
              }
            }
          }
          return item;
        });

        setChartDataArray(sortedData);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId, orderStats?.stats, paymentStats?.stats, registrationStats?.stats, selectedKpiData.dashBoardGraph, selectedKpiData.graphData, visitorStats?.stats]);

  // 장비 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        const machineDetails = await companyMachineUseCase.getMachineDetails(
          Number(companyId),
        );
        setCompanyMachines(machineDetails ?? []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId]);

  // 주문 데이터 로드 및 칸반 동기화
  const loadOrders = async () => {
    try {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      const result = await usecase.getOrders(cid);
      const mapped = result.items.map(mapApiOrderToView);
      setOrders(mapped);
      syncKanbanWithOrders(mapped);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("주문 불러오기 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    loadOrders();
  }, [companyId]);

  // 최근 일주일 이내의 주문인지 확인
  const isWithinOneWeek = (dateString: string): boolean => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return orderDate >= oneWeekAgo;
  };

  // 칸반 동기화 (최근 일주일 데이터만 표시)
  const syncKanbanWithOrders = (orderList: Order[]) => {
    const columnIds: KanbanStatus[] = [
      "accepted",
      "resubmission",
      "production",
      "production_complete",
      "shipping",
      "delivered",
    ];

    const updatedColumns = columnIds.map((columnId) => {
      // 배송 완료 컬럼만 최근 일주일 데이터 필터링 적용
      const relevantOrders = orderList.filter((order) => {
        if (order.status !== columnId) return false;
        if (columnId === "delivered") {
          return isWithinOneWeek(order.orderDate);
        }
        return true;
      });
      const cards = relevantOrders
        .map(orderToKanbanCard)
        .filter((card): card is KanbanCard => card !== null);

      return {
        id: columnId,
        title: KANBAN_STATUS_LABELS[columnId],
        count: cards.length,
        cards,
      };
    });

    setKanbanColumns(updatedColumns);

    // 배송 완료 카드들은 기본적으로 접힌 상태로 설정 (세션에 저장된 값이 없는 경우에만)
    const savedCollapsed = sessionStorage.getItem(COLLAPSED_CARDS_KEY);
    if (!savedCollapsed) {
      const deliveredColumn = updatedColumns.find(
        (col) => col.id === "delivered",
      );
      if (deliveredColumn && deliveredColumn.cards.length > 0) {
        const deliveredCardIds = deliveredColumn.cards.map((card) => card.id);
        setCollapsedCards(new Set(deliveredCardIds));
        sessionStorage.setItem(
          COLLAPSED_CARDS_KEY,
          JSON.stringify(deliveredCardIds),
        );
      }
    }
  };

  // 카드 클릭 - 상세 페이지로 이동
  const handleCardClick = (cardId: string) => {
    navigate(`/company/${companyId}/orders?mode=detail&orderId=${cardId}`);
  };

  // 드래그 관련
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeCardId = active.id as string;
    let overColumnId = over.id as string;

    // over가 카드인 경우, 해당 카드의 컬럼 찾기
    const sourceColumn = kanbanColumns.find((col) =>
      col.cards.some((card) => card.id === activeCardId),
    );

    // 유효한 컬럼인지 확인
    const validColumnIds: KanbanStatus[] = [
      "accepted",
      "resubmission",
      "production",
      "production_complete",
      "shipping",
      "delivered",
    ];

    // over가 카드 ID인 경우 해당 카드가 속한 컬럼 찾기
    if (!validColumnIds.includes(overColumnId as KanbanStatus)) {
      const targetColumn = kanbanColumns.find((col) =>
        col.cards.some((card) => card.id === overColumnId),
      );
      if (targetColumn) {
        overColumnId = targetColumn.id;
      } else {
        return;
      }
    }

    // 같은 컬럼이면 리턴
    if (sourceColumn?.id === overColumnId) return;

    const targetStatus = overColumnId as KanbanStatus;

    // 재접수인 경우 다이얼로그 표시
    if (targetStatus === "resubmission") {
      setPendingMove({ cardId: activeCardId, targetColumn: targetStatus });
      setResubmissionMessage("");
      setIsResubmissionOpen(true);
      return;
    }

    // 출고/배송인 경우 다이얼로그 표시
    if (targetStatus === "shipping") {
      setPendingMove({ cardId: activeCardId, targetColumn: targetStatus });
      setShippingCourier("");
      setShippingTrackingNumber("");
      setIsShippingDialogOpen(true);

      // 택배사 목록 로드
      if (courierList.length === 0) {
        setIsCourierLoading(true);
        try {
          const couriers = await usecase.getCourierList();
          setCourierList(couriers);
        } catch (e: any) {
          console.error(e);
        } finally {
          setIsCourierLoading(false);
        }
      }
      return;
    }

    // 그 외 상태 변경은 바로 API 호출
    await executeStatusChange(activeCardId, targetStatus);
  };

  // 상태 변경 실행
  const executeStatusChange = async (
    cardId: string,
    targetStatus: KanbanStatus,
  ) => {
    try {
      const cid = Number(companyId);
      const oid = Number(cardId);

      switch (targetStatus) {
        case "accepted":
          await usecase.acceptOrder(cid, oid);
          break;
        case "production":
          await usecase.startProduction(cid, oid);
          break;
        case "production_complete":
          await usecase.completeProduction(cid, oid);
          break;
        case "delivered":
          await usecase.completeDelivery(cid, oid);
          break;
        default:
          break;
      }

      await loadOrders();
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("상태 변경 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 재접수 확인
  const handleConfirmResubmission = async () => {
    if (!resubmissionMessage.trim()) {
      alert("수정 요청 사항을 입력해주세요");
      return;
    }

    if (!pendingMove) return;

    try {
      const cid = Number(companyId);
      const oid = Number(pendingMove.cardId);

      await usecase.resubmission(cid, oid, resubmissionMessage);
      await loadOrders();

      setIsResubmissionOpen(false);
      setResubmissionMessage("");
      setPendingMove(null);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("재접수 요청 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 출고/배송 확인
  const handleConfirmShipping = async () => {
    if (!pendingMove) return;

    try {
      const cid = Number(companyId);
      const oid = Number(pendingMove.cardId);

      await usecase.shipOrder(
        cid,
        oid,
        shippingCourier || undefined,
        shippingTrackingNumber || undefined,
      );
      await loadOrders();

      setIsShippingDialogOpen(false);
      setShippingCourier("");
      setShippingTrackingNumber("");
      setPendingMove(null);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("출고/배송 처리 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 다이얼로그 닫기
  const handleCloseResubmission = () => {
    setIsResubmissionOpen(false);
    setResubmissionMessage("");
    setPendingMove(null);
  };

  const handleCloseShipping = () => {
    setIsShippingDialogOpen(false);
    setShippingCourier("");
    setShippingTrackingNumber("");
    setPendingMove(null);
  };

  // 장비 상태 관련 헬퍼
  const formatRuntime = (runtimeSeconds: number | null): string => {
    if (!runtimeSeconds) return "0시간 0분";
    const hours = Math.floor(runtimeSeconds / 3600);
    const minutes = Math.floor((runtimeSeconds % 3600) / 60);
    return `${hours}시간 ${minutes}분`;
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case "ONLINE":
        return "온라인";
      case "OFFLINE":
        return "오프라인";
      case "WORKING":
        return "작동중";
      case "WARNING":
        return "경고";
      case "WARMUP":
        return "준비중";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "ONLINE":
      case "WORKING":
        return "#4285F4";
      case "OFFLINE":
        return "#9E9E9E";
      case "WARNING":
        return "#F4B400";
      case "WARMUP":
        return "#34A853";
      default:
        return "#4285F4";
    }
  };

  // 드래그 중인 카드 찾기
  const draggedCard = activeId
    ? kanbanColumns
        .flatMap((col) => col.cards)
        .find((card) => card.id === activeId)
    : null;

  return (
    <div className="w-full space-y-5 overflow-x-hidden">
      {/* 0. 온보딩 — 먼저 등록해야 하는 설정이 남아 있을 때만 보인다 */}
      {companyId && <CompanyOnboardingPanel companyId={Number(companyId)} />}

      {/* 1. 상단 - 2개 컬럼 (매출 통계 + 실시간 매출 현황) */}
      <div className="flex flex-col xl:flex-row gap-5">
        {/* 왼쪽 - 매출 통계 차트 */}
        <div className="bg-white rounded-[12px] border border-[#EEEEEE] shadow-[0_1px_2px_rgba(0,0,0,0.04)] pt-5 px-5 flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col items-start w-full md:flex-row md:items-center md:justify-between gap-2">
              <h3 className="text-[16px] font-semibold text-gray-900">
                실시간 통계
              </h3>
              <div className="inline-flex p-0.5 rounded-[8px] bg-[#F3F4F6]" role="tablist">
                {(["접속자수", "가입자수", "주문량", "결제금액"] as const).map((tab) => (
                  <button
                    key={tab}
                    role="tab"
                    aria-selected={activeTab === tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-2.5 h-[26px] text-[12px] rounded-[6px] transition-colors ${
                      activeTab === tab
                        ? "bg-white text-[#4285F4] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                        : "text-[#777] hover:text-[#333]"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 높이는 오른쪽 장비 모니터링(3대)과 맞춘다 */}
          <div className="h-[196px] w-[100%] translate-x-[-15px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartDataArray}
                margin={{ top: 4, right: 10, left: 10, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                />
                <YAxis tick={{ fontSize: 10 }} domain={[0, "dataMax + 10"]} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ fontWeight: "bold", marginBottom: "4px" }}
                />
                <Line
                  type="monotone"
                  dataKey={activeTabNames[activeTab]}
                  stroke="#4285F4"
                  strokeWidth={2}
                  dot={{ fill: "#4285F4", strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 오른쪽 - 실시간 장비 모니터링 (한 줄 행, 3대까지 보이고 나머지는 스크롤) */}
        <div className="bg-white rounded-[12px] border border-[#EEEEEE] shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-5 flex-1 min-w-0 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[16px] font-semibold text-gray-900 text-start">
              실시간 장비 모니터링
              {companyMachines.length > 0 && (
                <span className="ml-1.5 text-[13px] font-normal text-[#999]">{companyMachines.length}대</span>
              )}
            </h3>
            <button
              className="flex flex-row items-center gap-[0.234375rem] text-sm text-gray-500 hover:text-gray-700 transition-colors"
              onClick={() => navigate(`/company/${companyId}/equipments`)}
            >
              더보기
              <svg
                className="h-4 w-4 text-gray-400 rotate-[-90deg]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {companyMachines.length === 0 ? (
            <div className="h-[184px] flex items-center justify-center text-sm text-gray-400">
              장비가 존재하지 않습니다.
            </div>
          ) : (
            // 행 56px × 3 + 간격 8px × 2 = 184px
            <div className="h-[184px] overflow-y-auto pr-1 -mr-1 flex flex-col gap-2">
              {companyMachines.map((machine) => {
                const statusText = getStatusText(machine.status);
                const statusColor = getStatusColor(machine.status);
                const runtime = formatRuntime(machine.runtime);

                return (
                  <div
                    key={machine.machineId}
                    className="shrink-0 h-[56px] flex items-center gap-3 px-3.5 rounded-[8px] border border-[#E5E5E5] text-start"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-[#222] truncate">
                        {machine.displayName ?? machine.modelName}
                      </div>
                      <div className="text-[12px] text-[#999] truncate">{machine.manufacturer}</div>
                    </div>
                    <div className="shrink-0 text-right text-[12px] leading-[18px]">
                      <div className="font-medium" style={{ color: statusColor }}>
                        {statusText}
                      </div>
                      <div className="text-[#999] tabular-nums">
                        가동 <span style={{ color: statusColor }}>{runtime}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 2. 하단 - 작업 현황 칸반보드 */}
      <div className="bg-white rounded-[12px] border border-[#EEEEEE] shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-5">
        <div className="flex items-baseline gap-1.5 mb-3 text-left">
          <h2 className="text-[16px] font-semibold text-gray-900">작업 현황</h2>
          <span className="text-[13px] text-[#999] tabular-nums">
            {kanbanColumns.reduce((sum, column) => sum + column.count, 0)}건
          </span>
        </div>

        <div className="relative">
          <style>{`
            .kanban-scroll-container {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .kanban-scroll-container::-webkit-scrollbar {
              display: none;
              width: 0;
              height: 0;
            }
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes slideOutLeft {
              from {
                opacity: 1;
                transform: translateX(0);
              }
              to {
                opacity: 0;
                transform: translateX(-20px);
              }
            }
            @keyframes slideInRight {
              from {
                opacity: 0;
                transform: translateX(20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes slideOutRight {
              from {
                opacity: 1;
                transform: translateX(0);
              }
              to {
                opacity: 0;
                transform: translateX(20px);
              }
            }
            .scroll-btn-left {
              animation: slideInLeft 0.3s ease-out forwards;
            }
            .scroll-btn-left-hide {
              animation: slideOutLeft 0.3s ease-out forwards;
              pointer-events: none;
            }
            .scroll-btn-right {
              animation: slideInRight 0.3s ease-out forwards;
            }
            .scroll-btn-right-hide {
              animation: slideOutRight 0.3s ease-out forwards;
              pointer-events: none;
            }
          `}</style>

          {/* 왼쪽 스크롤 버튼 */}
          <button
            onClick={handleScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-r-full bg-white/80 shadow-[10px_10px_20px_-3px_rgba(0,0,0,0.1)]
              flex items-center justify-center
              hover:bg-white transition-colors
              ${canScrollLeft ? "scroll-btn-left" : "scroll-btn-left-hide"}`}
            style={{ display: canScrollLeft ? "flex" : undefined }}
            aria-label="이전 컬럼으로 이동"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* 오른쪽 스크롤 버튼 */}
          <button
            onClick={handleScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-l-full bg-white/80 shadow-[-10px_10px_20px_-3px_rgba(0,0,0,0.1)]
              flex items-center justify-center
              hover:bg-white transition-colors
              ${canScrollRight ? "scroll-btn-right" : "scroll-btn-right-hide"}`}
            style={{ display: canScrollRight ? "flex" : undefined }}
            aria-label="다음 컬럼으로 이동"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div
            ref={scrollContainerRef}
            className="kanban-scroll-container overflow-x-auto"
          >
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="flex gap-[0.625rem] justify-start min-w-max px-1 py-1">
                {kanbanColumns.map((column) => (
                  <DroppableColumn
                    key={column.id}
                    column={column}
                    onCardClick={handleCardClick}
                    collapsedCards={collapsedCards}
                    onToggleCard={handleToggleCard}
                  />
                ))}
              </div>

              <DragOverlay>
                {draggedCard ? <CardOverlay card={draggedCard} /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      </div>

      {/* 다이얼로그들 */}
      <ResubmissionDialog
        isOpen={isResubmissionOpen}
        message={resubmissionMessage}
        onMessageChange={setResubmissionMessage}
        onClose={handleCloseResubmission}
        onConfirm={handleConfirmResubmission}
      />

      <ShippingDialog
        isOpen={isShippingDialogOpen}
        courier={shippingCourier}
        trackingNumber={shippingTrackingNumber}
        courierList={courierList}
        isLoading={isCourierLoading}
        onCourierChange={setShippingCourier}
        onTrackingNumberChange={setShippingTrackingNumber}
        onClose={handleCloseShipping}
        onConfirm={handleConfirmShipping}
      />

      <ErrorMessageModal
        errorMessageTitle={errorMessageTitle}
        errorMessage={errorMessage}
        onClose={() => {
          setErrorMessageTitle("");
          setErrorMessage("");
        }}
      />
    </div>
  );
};
